import { Formik } from 'formik';
import _ from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import styled from 'styled-components';
import {
  ActionButton,
  Buttons,
  CancelButton,
  DeleteButton,
  Form,
  FormMessage,
  PromptNavigation,
  TooltipButton,
} from '~/components';
import { useApi, useConfirmation, useWorkspace } from '~/contexts';
import { useAuth, useFeatures, useForm } from '~/hooks';
import revenueRecognitionMethods from '~/lookups/revenue-recognition-methods';
import { PageLoader } from '~/routes/public/pages';
import { emptyStringToNull } from '~/utils';
import ProjectDeleteConfirmation from '../../ProjectDeleteConfirmation';
import SettingsActionsBanner from '../SettingsActionsBanner';
import getOverviewInitialValues from './initialValues';
import RecognizeRemainingRevenueConfirmation from './RecognizeRemainingRevenueConfirmation';
import { sectionValidationSchema } from './validation';

// Completing a fixed-fee project on one of these methods can leave scheduled revenue unrecognized, so the save
// offers to true it up. `invoiced` is excluded — it recognizes revenue straight from invoices.
const trueUpRecognitionMethods = [
  revenueRecognitionMethods.manual.id,
  revenueRecognitionMethods.automated_hours.id,
  revenueRecognitionMethods.automated_services_cost.id,
  revenueRecognitionMethods.automated_tm_basis.id,
];

const Delete = styled(TooltipButton)`
  margin-right: auto;
`;

// The shell every Overview section form is built on. Each section is its own route, its own Formik state and
// its own Save, but they all write through the one overview PATCH: a section sends the whole project so the
// fields it does not render keep their stored values, and a handler that reaches across sections — picking an
// internal client clears the billing type — still lands with the save that triggered it.
export default function OverviewForm({ project: initialProject, onSaved, section, fields: Fields, canDelete }) {
  const api = useApi();
  const auth = useAuth();
  const { workspace } = useWorkspace();
  const features = useFeatures();
  const confirmation = useConfirmation();
  const history = useHistory();
  const location = useLocation();
  const { clientKey, projectKey } = useParams();
  const [{ status, message, isSubmitting, saved }, form] = useForm();

  // Billing and Invoice Details are entirely `billing`; their fields render disabled without it, so the save
  // that would write them comes off too. Every other section carries general fields and keeps its Save.
  const isReadOnlySection = ['billing', 'invoice-details'].includes(section) && !auth.projects.sections.billing;

  const [project, setProject] = useState(null);
  const [pendingRedirect, setPendingRedirect] = useState(null);

  const fetchData = useCallback(async () => {
    const { data } = await api.www.workspaces(workspace.id).projectSettings(initialProject.id).overview();
    setProject(data);
    return data;
  }, [api, workspace.id, initialProject.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Deferred to an effect so the redirect fires after the submit handler resets the form — a history.replace
  // while the form is still dirty is blocked by the unsaved-changes Prompt.
  useEffect(() => {
    if (pendingRedirect) history.replace(pendingRedirect);
  }, [pendingRedirect, history]);

  if (!project) return <PageLoader />;

  const handleDelete = () => {
    confirmation.prompt((resolve) => (
      <ProjectDeleteConfirmation
        project={project}
        onClose={resolve}
        onDelete={() => {
          resolve(true);
          history.push(`/app/${workspace.key}/portfolio/projects`);
        }}
      />
    ));
  };

  const deleteBlockedReason =
    project.hasInvoices || project.hasCreditNotes
      ? 'This project is associated with one or more invoices or credit notes and cannot be deleted.'
      : project.hasOpportunities
        ? 'This project is associated with one or more opportunities and cannot be deleted.'
        : undefined;

  const initialValues = getOverviewInitialValues(project, { workspace, features });

  const handleSubmit = async (values, formik) => {
    try {
      let recognizeRemaining = false;
      const isCompleting = project.statusId !== 'completed' && values.statusId === 'completed';
      if (
        isCompleting &&
        auth.projects.sections.billing &&
        ['fixed', 'fixed_recurring'].includes(values.billingTypeId) &&
        trueUpRecognitionMethods.includes(values.revenueRecognitionMethod)
      ) {
        const { data: fixedFeeRevenue } = await api.www
          .workspaces(workspace.id)
          .projectSettings(project.id)
          .fixedFeeRevenue();

        if (fixedFeeRevenue.remainingRevenue > 0) {
          const choice = await confirmation.prompt((resolve) => (
            <RecognizeRemainingRevenueConfirmation
              currency={values.currency}
              billingScheduleRevenue={fixedFeeRevenue.billingScheduleRevenue}
              revenueRecognizedToDate={fixedFeeRevenue.recognizedRevenue}
              remainingRevenue={fixedFeeRevenue.remainingRevenue}
              onConfirm={resolve}
              onClose={() => resolve(false)}
            />
          ));

          if (!choice) return;
          recognizeRemaining = choice === 'yes';
        }
      }

      form.submit();

      const body = emptyStringToNull({
        ..._.omit(values, [
          'client',
          'email',
          'ccEmail',
          'invoiceTaxRate',
          'invoicePaymentTerm',
          'practice',
          'businessUnit',
          'projectGroup',
          'projectType',
          'purchaseOrder',
          'salesRepresentative',
          'tags',
          'qboProjectId',
          'accountingConnectionId',
          'accountingProvider',
        ]),
        accountingNotes: values.accountingNotes.trim(),
        clientId: values.client?.id ?? null,
        practiceId: values.practice?.id ?? null,
        businessUnitId: values.businessUnit?.id ?? null,
        invoiceTaxRateId: values.invoiceTaxRate?.id ?? null,
        invoicePaymentTermId: values.invoicePaymentTerm?.id ?? null,
        projectGroupId: values.projectGroup?.id ?? null,
        projectTypeId: values.projectType?.id ?? null,
        purchaseOrderId: values.purchaseOrder?.id ?? null,
        salesRepresentativeId: values.salesRepresentative?.id ?? null,
        projectTagAssignments: values.tags.map((tag) => ({ projectTagId: tag.id })),
        qboProjects: (values.accountingProvider !== 'qbo'
          ? project.qboProjects
          : values.qboProjectId
            ? [
                ...project.qboProjects.filter((p) => p.qboConnectionId !== values.accountingConnectionId),
                { qboConnectionId: values.accountingConnectionId, qboProjectId: values.qboProjectId },
              ]
            : project.qboProjects.filter((p) => p.qboConnectionId !== values.accountingConnectionId)
        ).map((p) => ({ qboConnectionId: p.qboConnectionId, qboProjectId: p.qboProjectId })),
      });

      // The true-up rides the same request so the status change and the ledger entry commit or fail as one.
      await api.www
        .workspaces(workspace.id)
        .projectSettings(project.id)
        .updateOverview(recognizeRemaining ? { ...body, recognizeRemainingRevenue: true } : body);

      const data = await fetchData();
      const pathChanged = data.key !== projectKey || data.client?.key !== clientKey;

      if (pathChanged) {
        form.save();
        formik.resetForm({ values });
        setPendingRedirect(
          location.pathname.replace(
            `/portfolio/projects/${clientKey}/${projectKey}`,
            `/portfolio/projects/${data.client.key}/${data.key}`,
          ),
        );
      } else {
        if (onSaved) await onSaved();
        form.save();
        formik.resetForm({ values });
      }
    } catch ({ message }) {
      form.error({ message });
    }
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validateOnBlur={false}
      validateOnChange={false}
      validationSchema={sectionValidationSchema(section)}>
      {(formik) => (
        <Form>
          <Fields project={project} formik={formik} />

          <SettingsActionsBanner>
            {canDelete && project.permissions.delete && (
              <Delete
                component={DeleteButton}
                disabled={!!deleteBlockedReason}
                tooltipPlacement="right"
                tooltip={deleteBlockedReason}
                onClick={handleDelete}>
                Delete
              </Delete>
            )}

            {status && <FormMessage.Error spaceRight>{message || 'An error has occurred.'}</FormMessage.Error>}

            {!isReadOnlySection && (
              <Buttons>
                <CancelButton disabled={!formik.dirty} onClick={() => formik.resetForm()}>
                  Cancel
                </CancelButton>

                <ActionButton isLoading={isSubmitting} ok={saved} onClick={formik.submitForm}>
                  Save
                </ActionButton>
              </Buttons>
            )}
          </SettingsActionsBanner>

          <PromptNavigation when={formik.dirty} />
        </Form>
      )}
    </Formik>
  );
}
